import { useState, type FormEvent } from "react";

import { apiClient, type DepartmentSummary } from "../../api/client";
import { errorMessage } from "../admin/errors";
import { isValidDepartmentCode, suggestDepartmentCode } from "./codeSuggestion";
import { useModalFocusTrap } from "./useModalFocusTrap";

interface AddDepartmentDialogProps {
  onClose: () => void;
  onCreated: (department: DepartmentSummary) => Promise<void> | void;
}

export function AddDepartmentDialog({ onClose, onCreated }: AddDepartmentDialogProps) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [manuallyEdited, setManuallyEdited] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const dialog = useModalFocusTrap<HTMLElement>("[data-initial-focus]");

  function changeName(value: string) {
    setName(value);
    setConfirmed(false);
    if (!manuallyEdited) setCode(suggestDepartmentCode(value));
  }

  function changeCode(value: string) {
    setCode(value);
    setManuallyEdited(true);
    setConfirmed(false);
  }

  function regenerateCode() {
    setCode(suggestDepartmentCode(name));
    setManuallyEdited(false);
    setConfirmed(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!confirmed || !name.trim() || !isValidDepartmentCode(code)) return;
    setSubmitting(true);
    setError("");
    try {
      const created = await apiClient.createDepartment({ name: name.trim(), code });
      await onCreated(created);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  const codeInvalid = Boolean(code) && !isValidDepartmentCode(code);
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialog}
        aria-labelledby="add-department-title"
        aria-modal="true"
        className="modal-card department-create-dialog"
        onKeyDown={(event) => {
          if (event.key === "Escape" && !submitting) onClose();
        }}
        role="dialog"
        tabIndex={-1}
      >
        <div className="panel-heading">
          <div>
            <span className="eyebrow">稳定组织标识</span>
            <h2 id="add-department-title">新增部门</h2>
          </div>
          <button
            aria-label="关闭新增部门"
            className="text-button"
            disabled={submitting}
            onClick={onClose}
            type="button"
          >
            关闭
          </button>
        </div>
        <form className="department-form" onSubmit={(event) => void submit(event)}>
          <label>
            部门名称
            <input
              data-initial-focus
              maxLength={160}
              onChange={(event) => changeName(event.target.value)}
              required
              value={name}
            />
          </label>
          <label>
            部门编码
            <input
              aria-describedby="department-code-help"
              aria-invalid={codeInvalid}
              maxLength={64}
              onChange={(event) => changeCode(event.target.value)}
              required
              value={code}
            />
          </label>
          <div className="department-code-guidance" id="department-code-help">
            <p>编码创建后不可修改。使用 2 至 64 位小写字母、数字和单个连字符，并以字母开头。</p>
            <button disabled={!name.trim() || submitting} onClick={regenerateCode} type="button">
              重新生成编码
            </button>
          </div>
          {codeInvalid && (
            <p className="field-error" role="alert">
              当前编码不符合格式要求。
            </p>
          )}
          <label className="department-code-confirmation">
            <input
              checked={confirmed}
              disabled={!isValidDepartmentCode(code) || submitting}
              onChange={(event) => setConfirmed(event.target.checked)}
              type="checkbox"
            />
            我确认最终编码为“{code || "尚未生成"}”，并理解创建后不可修改。
          </label>
          {error && (
            <p className="alert error" role="alert">
              {error}
            </p>
          )}
          <div className="modal-actions">
            <button disabled={submitting} onClick={onClose} type="button">
              取消
            </button>
            <button
              disabled={submitting || !confirmed || !name.trim() || !isValidDepartmentCode(code)}
              type="submit"
            >
              {submitting ? "正在创建…" : "创建部门"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
